import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchVisitorPricingConfig,
  saveVisitorPricingConfig,
  resetVisitorPricingConfig,
  VisitorPricingConfig as VisitorPricingConfigType,
  PricingPhase,
} from '../../lib/api';
import {
  Ticket,
  Clock,
  Sparkles,
  Save,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Calendar,
  IndianRupee,
  ShieldCheck,
  TrendingDown,
  Layers,
  ArrowRight,
} from 'lucide-react';

interface VisitorPricingConfigProps {
  adminKey: string;
}

export function VisitorPricingConfig({ adminKey }: VisitorPricingConfigProps) {
  const queryClient = useQueryClient();

  const [phases, setPhases] = useState<PricingPhase[]>([]);
  const [spotPrices, setSpotPrices] = useState<{
    trade_visitor: number;
    conference_delegate: number;
    vip: number;
  }>({
    trade_visitor: 3000,
    conference_delegate: 10500,
    vip: 20000,
  });
  const [gstRatePercent, setGstRatePercent] = useState<number>(18);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch current visitor pricing configuration
  const { data: configData, isLoading } = useQuery<VisitorPricingConfigType>({
    queryKey: ['admin-visitor-pricing', adminKey],
    queryFn: () => fetchVisitorPricingConfig(adminKey),
    enabled: !!adminKey,
  });

  // Sync server data to local state
  React.useEffect(() => {
    if (configData?.phases) {
      setPhases(configData.phases);
      if (configData.spotPrices) setSpotPrices(configData.spotPrices);
      if (configData.gstRatePercent) setGstRatePercent(configData.gstRatePercent);
    }
  }, [configData]);

  // Mutation: Save visitor pricing
  const saveMutation = useMutation({
    mutationFn: () =>
      saveVisitorPricingConfig(
        {
          phases,
          spotPrices,
          gstRatePercent,
        },
        adminKey
      ),
    onSuccess: () => {
      setStatusMessage({
        type: 'success',
        text: 'Visitor & Delegate pricing schedule updated successfully across registration portals!',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-visitor-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['pricing-schedule'] });
      setTimeout(() => setStatusMessage(null), 4000);
    },
    onError: (err: any) => {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to update visitor pricing schedule' });
    },
  });

  // Mutation: Reset visitor pricing
  const resetMutation = useMutation({
    mutationFn: () => resetVisitorPricingConfig(adminKey),
    onSuccess: (data) => {
      setPhases(data.phases);
      if (data.spotPrices) setSpotPrices(data.spotPrices);
      setStatusMessage({
        type: 'success',
        text: 'Visitor pricing schedule restored to default IIAB 2027 6-phase dynamic schedule.',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-visitor-pricing'] });
      queryClient.invalidateQueries({ queryKey: ['pricing-schedule'] });
      setTimeout(() => setStatusMessage(null), 4000);
    },
    onError: (err: any) => {
      setStatusMessage({ type: 'error', text: err.message || 'Failed to reset visitor pricing' });
    },
  });

  const handlePhaseFieldChange = (phaseIdx: number, field: keyof PricingPhase, val: any) => {
    setPhases((prev) => {
      const next = [...prev];
      next[phaseIdx] = { ...next[phaseIdx], [field]: val };
      return next;
    });
  };

  const handlePhasePriceChange = (
    phaseIdx: number,
    tier: 'trade_visitor' | 'conference_delegate' | 'vip',
    val: number
  ) => {
    setPhases((prev) => {
      const next = [...prev];
      const p = next[phaseIdx];
      const newPrices = { ...p.prices, [tier]: Math.max(0, val) };
      const newSavings = {
        trade_visitor: Math.max(0, spotPrices.trade_visitor - newPrices.trade_visitor),
        conference_delegate: Math.max(0, spotPrices.conference_delegate - newPrices.conference_delegate),
        vip: Math.max(0, spotPrices.vip - newPrices.vip),
      };

      next[phaseIdx] = {
        ...p,
        prices: newPrices,
        savingsVsSpot: newSavings,
      };
      return next;
    });
  };

  const nowTime = Date.now();

  return (
    <div className="space-y-8 animate-in fade-in">
      {/* Top Banner & Header */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
        <div>
          <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
            <Ticket className="w-5 h-5 text-[#0A3D27]" />
            <span>Dynamic Temporal Visitor & Delegate Pricing Engine</span>
          </h3>
          <p className="text-xs text-slate-600 mt-0.5">
            Manage advance cutoff windows, tier discounts (Super Early Bird to Spot Rate), and box office reference prices.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Reset visitor pricing to standard default 6-phase schedule?')) {
                resetMutation.mutate();
              }
            }}
            disabled={resetMutation.isPending}
            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs transition-colors border border-slate-200 cursor-pointer"
            title="Reset to Default Schedule"
          >
            <RotateCcw className={`w-4 h-4 ${resetMutation.isPending ? 'animate-spin text-[#C5A059]' : ''}`} />
          </button>

          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-xs transition-all shadow-sm shadow-[#0A3D27]/20 cursor-pointer disabled:opacity-50"
          >
            <Save className={`w-3.5 h-3.5 ${saveMutation.isPending ? 'animate-spin' : ''}`} />
            <span>{saveMutation.isPending ? 'Saving...' : 'Save Pricing Schedule'}</span>
          </button>
        </div>
      </div>

      {statusMessage && (
        <div
          className={`p-3.5 rounded-xl text-xs flex items-center justify-between animate-in fade-in shadow-xs ${
            statusMessage.type === 'success'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border border-rose-200 text-rose-800'
          }`}
        >
          <div className="flex items-center gap-2 font-semibold">
            {statusMessage.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-[#0A3D27] shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span>{statusMessage.text}</span>
          </div>
          <button onClick={() => setStatusMessage(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
            ✕
          </button>
        </div>
      )}

      {/* SECTION 1: BENCHMARK SPOT / BOX OFFICE RATES */}
      <div className="bg-white border border-slate-200/90 rounded-3xl p-5 sm:p-6 space-y-4 shadow-xs">
        <div>
          <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C5A059]" />
            <span>Benchmark Onsite / Spot Box Office Rates</span>
          </h4>
          <p className="text-xs text-slate-600 mt-0.5">
            Full-fare baseline rates used to dynamically calculate discount badges and attendee savings across advance phases.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-[#F8F9F6] p-4 rounded-2xl border border-slate-200/90 space-y-1.5">
            <span className="text-[11px] font-bold text-slate-600 uppercase">Trade Visitor Pass</span>
            <div className="flex items-center gap-1 font-mono text-base font-bold text-[#0A3D27]">
              <span>₹</span>
              <input
                type="number"
                step={500}
                value={spotPrices.trade_visitor}
                onChange={(e) => setSpotPrices((prev) => ({ ...prev, trade_visitor: Number(e.target.value) }))}
                className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-sm font-mono text-slate-900 focus:outline-none focus:border-[#0A3D27]"
              />
            </div>
            <span className="text-[10px] text-slate-500 block">+18% GST applicable</span>
          </div>

          <div className="bg-[#F8F9F6] p-4 rounded-2xl border border-slate-200/90 space-y-1.5">
            <span className="text-[11px] font-bold text-slate-600 uppercase">Conference Delegate Pass</span>
            <div className="flex items-center gap-1 font-mono text-base font-bold text-slate-900">
              <span>₹</span>
              <input
                type="number"
                step={500}
                value={spotPrices.conference_delegate}
                onChange={(e) => setSpotPrices((prev) => ({ ...prev, conference_delegate: Number(e.target.value) }))}
                className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-sm font-mono text-slate-900 focus:outline-none focus:border-[#0A3D27]"
              />
            </div>
            <span className="text-[10px] text-slate-500 block">+18% GST applicable</span>
          </div>

          <div className="bg-[#F8F9F6] p-4 rounded-2xl border border-slate-200/90 space-y-1.5">
            <span className="text-[11px] font-bold text-slate-600 uppercase">VIP All-Access Pass</span>
            <div className="flex items-center gap-1 font-mono text-base font-bold text-[#785613]">
              <span>₹</span>
              <input
                type="number"
                step={500}
                value={spotPrices.vip}
                onChange={(e) => setSpotPrices((prev) => ({ ...prev, vip: Number(e.target.value) }))}
                className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-sm font-mono text-slate-900 focus:outline-none focus:border-[#0A3D27]"
              />
            </div>
            <span className="text-[10px] text-slate-500 block">+18% GST applicable</span>
          </div>
        </div>
      </div>

      {/* SECTION 2: 6 DYNAMIC CUTOFF PHASES */}
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#0A3D27]" />
            <span>Temporal Cutoff Windows (6 Registration Phases)</span>
          </h4>
          <p className="text-xs text-slate-600 mt-0.5">
            The registration portal automatically resolves visitor pricing against the current UTC timestamp.
          </p>
        </div>

        <div className="space-y-4">
          {phases.map((phase, idx) => {
            const startTime = new Date(phase.startDate).getTime();
            const endTime = new Date(phase.endDate).getTime();
            const isActive = nowTime >= startTime && nowTime <= endTime;
            const isPast = nowTime > endTime;

            return (
              <div
                key={phase.id}
                className={`border rounded-3xl p-5 sm:p-6 space-y-4 shadow-xs transition-all ${
                  isActive
                    ? 'border-[#0A3D27] ring-2 ring-[#0A3D27]/15 bg-emerald-50/20'
                    : 'bg-white border-slate-200/90 hover:border-slate-300'
                }`}
              >
                {/* Phase Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-lg bg-slate-100 text-slate-700 text-xs font-black flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={phase.name}
                          onChange={(e) => handlePhaseFieldChange(idx, 'name', e.target.value)}
                          className="text-base font-bold text-slate-900 bg-transparent border-b border-transparent focus:border-[#0A3D27] focus:outline-none"
                        />
                        {isActive && (
                          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0A3D27]/10 text-[#0A3D27] border border-[#0A3D27]/30">
                            ACTIVE LIVE NOW
                          </span>
                        )}
                        {isPast && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                            EXPIRED
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-bold text-slate-600">Badge Label:</label>
                    <input
                      type="text"
                      value={phase.badge}
                      onChange={(e) => handlePhaseFieldChange(idx, 'badge', e.target.value)}
                      placeholder="e.g. Save 50%"
                      className="bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs text-[#785613] font-bold focus:outline-none focus:border-[#0A3D27]"
                    />
                  </div>
                </div>

                {/* Date Controls */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                      Start Cutoff (ISO-8601 UTC)
                    </label>
                    <input
                      type="text"
                      value={phase.startDate}
                      onChange={(e) => handlePhaseFieldChange(idx, 'startDate', e.target.value)}
                      className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-mono text-[11px] focus:outline-none focus:border-[#0A3D27]"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                      End Cutoff (ISO-8601 UTC)
                    </label>
                    <input
                      type="text"
                      value={phase.endDate}
                      onChange={(e) => handlePhaseFieldChange(idx, 'endDate', e.target.value)}
                      className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-mono text-[11px] focus:outline-none focus:border-[#0A3D27]"
                    />
                  </div>
                </div>

                {/* 3 Tier Prices in this Phase */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                  {/* Trade Visitor */}
                  <div className="bg-[#F8F9F6] p-3.5 rounded-2xl border border-slate-200/90 space-y-1">
                    <div className="flex items-center justify-between text-[11px] text-slate-600 font-bold">
                      <span>Trade Visitor</span>
                      <span className="text-[10px] text-[#0A3D27] font-semibold">
                        Save ₹{(phase.savingsVsSpot?.trade_visitor || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold text-slate-500">₹</span>
                      <input
                        type="number"
                        value={phase.prices?.trade_visitor}
                        onChange={(e) => handlePhasePriceChange(idx, 'trade_visitor', Number(e.target.value))}
                        className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-mono font-bold text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                      />
                    </div>
                    <span className="text-[10px] text-slate-500 block">
                      +18% GST = ₹{Math.round((Number(phase.prices?.trade_visitor) || 0) * 1.18).toLocaleString('en-IN')}
                    </span>
                  </div>

                  {/* Conference Delegate */}
                  <div className="bg-[#F8F9F6] p-3.5 rounded-2xl border border-slate-200/90 space-y-1">
                    <div className="flex items-center justify-between text-[11px] text-slate-600 font-bold">
                      <span>Conference Delegate</span>
                      <span className="text-[10px] text-slate-700 font-semibold">
                        Save ₹{(phase.savingsVsSpot?.conference_delegate || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold text-slate-500">₹</span>
                      <input
                        type="number"
                        value={phase.prices?.conference_delegate}
                        onChange={(e) => handlePhasePriceChange(idx, 'conference_delegate', Number(e.target.value))}
                        className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-mono font-bold text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                      />
                    </div>
                    <span className="text-[10px] text-slate-500 block">
                      +18% GST = ₹{Math.round((Number(phase.prices?.conference_delegate) || 0) * 1.18).toLocaleString('en-IN')}
                    </span>
                  </div>

                  {/* VIP All-Access */}
                  <div className="bg-[#F8F9F6] p-3.5 rounded-2xl border border-slate-200/90 space-y-1">
                    <div className="flex items-center justify-between text-[11px] text-slate-600 font-bold">
                      <span>VIP All-Access</span>
                      <span className="text-[10px] text-[#785613] font-semibold">
                        Save ₹{(phase.savingsVsSpot?.vip || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs font-bold text-slate-500">₹</span>
                      <input
                        type="number"
                        value={phase.prices?.vip}
                        onChange={(e) => handlePhasePriceChange(idx, 'vip', Number(e.target.value))}
                        className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1 text-xs font-mono font-bold text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                      />
                    </div>
                    <span className="text-[10px] text-slate-500 block">
                      +18% GST = ₹{Math.round((Number(phase.prices?.vip) || 0) * 1.18).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
